import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faBalanceScale,
  faBoxesStacked,
  faCalculator,
  faCashRegister,
  faChartLine,
  faCheckCircle,
  faCircleNodes,
  faFileInvoiceDollar,
  faShieldHalved,
  faStore,
  faTruckRampBox,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import "./BillingSoftware.css";
import "./FeaturesSolutions.css";

const path = "/features-solutions";
const title = "Business Software Features & Solutions | Auditus-iERP";
const description =
  "Explore Auditus-iERP business software features for billing, accounting, inventory, POS, GST, sales, purchases, customers, suppliers, payments, expenses, and reports.";

const modules = [
  [faFileInvoiceDollar, "Billing", "GST-ready invoice and billing workflows for daily transactions.", "/billing-software"],
  [faCalculator, "Accounting", "Ledgers, vouchers, payments, expenses, and financial reports.", "/accounting-software"],
  [faBoxesStacked, "Inventory", "Products, stock tracking, batches, warehouses, barcode movement, and item reports.", "/inventory-management-software"],
  [faCashRegister, "POS", "Fast retail counter billing with products, customers, payments, and stock visibility.", "/pos-software"],
  [faBalanceScale, "GST", "GST-ready invoices, GSTIN, HSN/SAC, e-invoice, e-way bill, and GST reports.", "/gst-software"],
  [faChartLine, "Reports", "Sales, purchase, inventory, GST, financial, audit, and dashboard reports.", "/erp-software"],
  [faTruckRampBox, "Purchases", "Purchase entries, supplier records, incoming stock, and purchase reporting.", "/erp-software"],
  [faUsers, "Customers & Suppliers", "Party records, ledgers, balances, payment references, and transaction history.", "/erp-software"],
];

const solutionBlocks = [
  {
    icon: faFileInvoiceDollar,
    title: "Billing & Invoicing",
    text:
      "Create GST-ready sales and purchase invoices, select products, add customer details, record payment references, and keep billing information connected with stock and accounts.",
    link: "/billing-software",
    linkText: "Explore billing software",
  },
  {
    icon: faCalculator,
    title: "Accounting & Financial Management",
    text:
      "Maintain ledgers, vouchers, expenses, payments, receivables, payables, cash activity, and financial reports from the same business data used in sales and purchases.",
    link: "/accounting-software",
    linkText: "Explore accounting software",
  },
  {
    icon: faBoxesStacked,
    title: "Inventory & Stock Management",
    text:
      "Manage products, stock movement, warehouses, batches, reorder levels, barcode workflows, purchase-related inventory, sales-related inventory, and item-wise reports.",
    link: "/inventory-management-software",
    linkText: "Explore inventory management",
  },
  {
    icon: faCashRegister,
    title: "POS & Retail Management",
    text:
      "Support fast POS billing, barcode product selection, retail sales, payment recording, inventory connection, and customer-facing counter workflows.",
    link: "/pos-software",
    linkText: "Explore POS software",
  },
  {
    icon: faBalanceScale,
    title: "GST & Tax Management",
    text:
      "Use GST-ready billing, tax calculation workflows, GSTIN details, HSN/SAC handling, GST reports, e-invoice workflows, and e-way bill workflows.",
    link: "/gst-software",
    linkText: "Explore GST software",
  },
];

const operations = [
  ["Sales Management", "Record sales transactions, customer sales, sales history, sales tracking, returns, discounts, and sales reports."],
  ["Purchase Management", "Manage purchase transactions, supplier records, incoming stock, purchase tracking, purchase records, and purchase reports."],
  ["Customer Management", "Keep customer records, customer ledgers, balances, payment references, and transaction history organized."],
  ["Supplier Management", "Manage supplier records, purchase activity, balances, payment references, and supplier-wise reports."],
  ["Expense Management", "Record day-to-day expenses, cash activity, fund flow, and operational outflow for better financial visibility."],
  ["Payment Management", "Track payment records, customer payments, supplier payments, outstanding amounts, and payment references."],
];

const problemSolutions = [
  ["Manual billing", "Digital GST-ready billing", "Faster invoice processing and cleaner transaction records."],
  ["Disconnected stock records", "Integrated inventory with sales and purchases", "Better stock visibility across daily operations."],
  ["Manual financial records", "Connected ledgers, vouchers, payments, and expenses", "Better financial organization for owners and accounts teams."],
  ["Scattered reports", "Centralized business reporting", "Easier monitoring across sales, purchase, stock, GST, and accounts."],
];

const categories = [
  ["Business Operations", ["Billing", "Sales", "Purchases", "Quotations and estimates", "Sales orders", "Purchase orders"]],
  ["Financial Management", ["Accounting", "Payments", "Expenses", "Receivables", "Payables", "Cash and fund flow"]],
  ["Inventory", ["Products", "Stock tracking", "Warehouses", "Batches", "Barcode movement", "Inventory reports"]],
  ["Retail", ["POS billing", "Barcode workflows", "Retail sales", "Customer records", "Payment recording"]],
  ["Tax", ["GST-ready invoices", "GSTIN details", "HSN/SAC", "E-invoice workflows", "E-way bill workflows", "GST reports"]],
  ["Management", ["Customers", "Suppliers", "Ledgers", "Balances", "Business reports", "Dashboard reports"]],
];

const screenshots = [
  ["/images/screenshots/dashboard.png", "Auditus-iERP business management dashboard", "Business dashboard"],
  ["/images/screenshots/invoice.png", "Auditus-iERP billing software screen", "Billing and POS screen"],
  ["/images/screenshots/reports.png", "Auditus-iERP business reporting screen", "Reports screen"],
  ["/images/screenshots/expenses.png", "Auditus-iERP expense management screen", "Expense screen"],
  ["/images/gstdashboard.PNG", "Auditus-iERP GST software dashboard", "GST dashboard"],
];

const faqs = [
  ["What features does Auditus-iERP provide?", "Auditus-iERP provides billing, accounting, inventory, POS, GST, sales, purchases, customer management, supplier management, payments, expenses, and business reports."],
  ["What business operations can Auditus-iERP manage?", "It can manage everyday operations such as invoices, sales, purchases, products, stock, ledgers, payments, expenses, GST workflows, customers, suppliers, and reports."],
  ["Does Auditus-iERP include billing?", "Yes. Auditus-iERP includes GST-ready billing, invoice creation, product selection, customer details, payment recording, and connected sales records."],
  ["Does Auditus-iERP include accounting?", "Yes. It includes accounting workflows for ledgers, vouchers, sales, purchases, expenses, payments, GST-related records, and financial reports."],
  ["Does Auditus-iERP include inventory management?", "Yes. It includes product and stock management, barcode movement, warehouses, batches, reorder levels, stock tracking, and inventory reports."],
  ["Does Auditus-iERP include POS?", "Yes. Auditus-iERP includes POS billing for retail workflows with product selection, barcode support, payment recording, customers, and inventory connection."],
  ["Does Auditus-iERP support GST-related billing?", "Yes. It supports GST-ready invoices, tax calculation workflows, GSTIN details, HSN/SAC handling, GST reports, e-invoice workflows, and e-way bill workflows."],
  ["Can billing and accounting work together?", "Yes. Billing transactions can support accounting records such as ledgers, vouchers, payment references, financial reports, and GST-related records."],
  ["Can inventory and billing work together?", "Yes. Inventory connects with billing through products, stock tracking, sales, purchases, barcode movement, and item-wise reporting."],
  ["Can businesses manage customers and suppliers?", "Yes. Businesses can manage customer and supplier records, ledgers, balances, transaction history, purchase activity, sales activity, and payment references."],
  ["Does Auditus-iERP provide business reports?", "Yes. Auditus-iERP includes sales, purchase, inventory, GST, financial, audit, item-wise, and dashboard reports."],
  ["Can Auditus-iERP be used by retail businesses?", "Yes. Retail businesses can use billing, POS, barcode workflows, products, customers, payments, inventory, GST workflows, and reports."],
  ["Can Auditus-iERP be used by supermarkets?", "Yes. Supermarkets can use fast billing, POS, barcode workflows, product records, inventory, purchases, sales tracking, payments, and reports."],
  ["Can Auditus-iERP be used by textile businesses?", "Yes. Textile businesses can use billing, products, inventory, purchases, accounting, GST workflows, customers, suppliers, and reports."],
  ["Can I get a demo of Auditus-iERP?", "Yes. Use the Book a Demo button to open the demo request form and share your business requirements."],
];

function setMeta(name, content, attribute = "name") {
  let tag = document.head.querySelector(`meta[${attribute}="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setLink(rel, href) {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

export default function FeaturesSolutions() {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;
    setMeta("description", description);
    setMeta("robots", "index, follow");
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", path, "property");
    setMeta("og:image", "/images/screenshots/dashboard.png", "property");
    setLink("canonical", path);

    const schema = document.createElement("script");
    schema.id = "features-solutions-schema";
    schema.type = "application/ld+json";
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "/" },
            { "@type": "ListItem", position: 2, name: "Features & Business Solutions", item: path },
          ],
        },
        {
          "@type": "SoftwareApplication",
          name: "Auditus-iERP Features & Business Solutions",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Windows, Android",
          url: path,
          image: "/images/screenshots/dashboard.png",
          description,
          featureList: [
            ...modules.map((item) => item[1]),
            ...operations.map((item) => item[0]),
          ],
        },
        {
          "@type": "FAQPage",
          mainEntity: faqs.map(([q, a]) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        },
      ],
    });
    document.head.appendChild(schema);

    return () => {
      document.title = previousTitle;
      schema.remove();
    };
  }, []);

  return (
    <main className="billing-page fs-page">
      <section className="fs-hero" aria-labelledby="features-solutions-title">
        <div className="billing-container fs-hero-grid">
          <div className="fs-hero-copy">
            <nav className="billing-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Features & Business Solutions</span>
            </nav>
            <p className="billing-kicker">
              <FontAwesomeIcon icon={faCircleNodes} aria-hidden="true" />
              Business software features
            </p>
            <h1 id="features-solutions-title">Powerful Business Features. One Integrated Solution.</h1>
            <p className="billing-lead">
              Auditus-iERP brings essential business operations together in one platform:
              billing, accounting, inventory, POS, GST, sales, purchases, customers,
              suppliers, payments, expenses, and reports.
            </p>
            <div className="billing-actions">
              <Link to="/Demo" className="billing-btn billing-btn-primary">
                Book a Demo <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </Link>
              <Link to="/Trial" className="billing-btn billing-btn-secondary">
                Get Started
              </Link>
            </div>
            <div className="billing-pills" aria-label="Auditus-iERP verified modules">
              {["Billing", "Accounting", "Inventory", "POS", "GST", "Sales", "Purchases", "Reports"].map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>

          <div className="billing-window fs-hero-window">
            <div className="billing-window-bar">
              <span>Auditus-iERP - Business Management</span>
              <i />
            </div>
            <img
              src="/images/screenshots/dashboard.png"
              alt="Auditus-iERP business management dashboard"
              width="920"
              height="560"
              loading="eager"
            />
            <div className="billing-window-foot">
              <span>Sales</span>
              <span>Stock</span>
              <span>Accounts</span>
              <span>Reports</span>
            </div>
          </div>
        </div>
      </section>

      <section className="billing-section fs-overview" aria-labelledby="everything-platform">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">What Auditus-iERP can do</p>
            <h2 id="everything-platform">Everything Your Business Needs in One Platform</h2>
            <p>
              The major Auditus-iERP modules are designed to work together, so business
              management software tasks like billing, stock, accounts, GST, payments,
              and reporting stay connected instead of scattered.
            </p>
          </div>
          <div className="fs-module-grid">
            {modules.map(([icon, name, text, link]) => (
              <Link to={link} className="fs-module-card" key={name}>
                <span className="billing-feature-icon">
                  <FontAwesomeIcon icon={icon} aria-hidden="true" />
                </span>
                <strong>{name}</strong>
                <p>{text}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="solution-sections">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Core solutions</p>
            <h2 id="solution-sections">Business Management Solutions for Daily Work</h2>
            <p>
              Each solution below is based on capabilities already described in the
              current Auditus-iERP product pages and feature data.
            </p>
          </div>
          <div className="fs-solution-grid">
            {solutionBlocks.map((item) => (
              <article className="fs-solution-card" key={item.title}>
                <div className="fs-solution-icon">
                  <FontAwesomeIcon icon={item.icon} aria-hidden="true" />
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
                <Link to={item.link}>
                  {item.linkText} <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section fs-operations" aria-labelledby="operations-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Operational control</p>
            <h2 id="operations-title">Sales, Purchases, Customers, Suppliers, Expenses & Payments</h2>
            <p>
              Auditus-iERP connects front-office transactions with back-office records,
              giving teams a practical business management solution for everyday work.
            </p>
          </div>
          <div className="fs-operation-grid">
            {operations.map(([name, text]) => (
              <article key={name}>
                <FontAwesomeIcon icon={faCheckCircle} aria-hidden="true" />
                <h3>{name}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="reports-title">
        <div className="billing-container billing-two-col">
          <div>
            <p className="billing-kicker">Business reporting</p>
            <h2 id="reports-title">Business Reports & Insights</h2>
          </div>
          <div className="billing-copy">
            <p>
              Auditus-iERP includes sales reports, purchase reports, inventory reports,
              item-wise reports, GST reports, financial reports, audit reports, cash
              flow reports, fund flow reports, and dashboard reports.
            </p>
            <p>
              These reports help owners and teams monitor business activity from the
              same connected records used for billing, purchases, inventory, payments,
              expenses, and accounts.
            </p>
          </div>
        </div>
      </section>

      <section className="billing-section fs-automation" aria-labelledby="automation-title">
        <div className="billing-container billing-two-col">
          <div>
            <p className="billing-kicker">Connected calculations</p>
            <h2 id="automation-title">Automate Everyday Business Work</h2>
          </div>
          <div className="billing-copy">
            <p>
              Auditus-iERP supports automatic tax calculation workflows, connected
              stock updates from sales and purchases, reorder-level review, recurring
              accounting entries, year-end balance carry-forward, and report generation
              from transaction data.
            </p>
            <p>
              The automation focus is practical: reduce repeated entry, keep records
              linked, and make routine business summaries easier to prepare.
            </p>
          </div>
        </div>
      </section>

      <section className="billing-section fs-problem-solution" aria-labelledby="problem-solution-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Problem to solution</p>
            <h2 id="problem-solution-title">Feature to Business Problem to Solution</h2>
          </div>
          <div className="fs-flow-grid">
            {problemSolutions.map(([problem, solution, benefit]) => (
              <article key={problem}>
                <div>
                  <span>Business Problem</span>
                  <strong>{problem}</strong>
                </div>
                <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                <div>
                  <span>Auditus-iERP Solution</span>
                  <strong>{solution}</strong>
                </div>
                <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                <div>
                  <span>Business Benefit</span>
                  <strong>{benefit}</strong>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section fs-connected-system" aria-labelledby="connected-system-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Connected system</p>
            <h2 id="connected-system-title">One Connected Business System</h2>
            <p>
              Auditus-iERP connects the workflows that businesses use most often, from
              sales and billing to inventory, payment, accounting, purchases, suppliers,
              and reports.
            </p>
          </div>
          <div className="fs-workflow-pair">
            <div className="fs-workflow-card" aria-label="Sales workflow">
              {["Sales", "Billing", "Inventory", "Payment", "Accounting", "Reports"].map((step, index) => (
                <React.Fragment key={step}>
                  <span>{step}</span>
                  {index < 5 && <i aria-hidden="true" />}
                </React.Fragment>
              ))}
            </div>
            <div className="fs-workflow-card" aria-label="Purchase workflow">
              {["Purchases", "Inventory", "Supplier", "Payment", "Accounting", "Reports"].map((step, index) => (
                <React.Fragment key={step}>
                  <span>{step}</span>
                  {index < 5 && <i aria-hidden="true" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="categories-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Feature categories</p>
            <h2 id="categories-title">Auditus-iERP Feature Categories</h2>
          </div>
          <div className="fs-category-grid">
            {categories.map(([name, items]) => (
              <article key={name}>
                <h3>{name}</h3>
                <ul>
                  {items.map((item) => (
                    <li key={item}>
                      <FontAwesomeIcon icon={faCheckCircle} aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section fs-industries" aria-labelledby="business-needs-title">
        <div className="billing-container billing-two-col">
          <div>
            <p className="billing-kicker">Industry-ready workflows</p>
            <h2 id="business-needs-title">Solutions for Different Business Needs</h2>
            <p>
              Auditus-iERP supports supermarkets, retail businesses, textile businesses,
              wholesale businesses, distributors, grocery businesses, and small businesses
              that need connected billing, stock, accounts, GST, and reports.
            </p>
            <Link to="/industries" className="billing-btn billing-btn-secondary">
              Explore industry solutions
            </Link>
          </div>
          <div className="fs-industry-list">
            {["Supermarkets", "Retail businesses", "Textile businesses", "Wholesale businesses", "Distributors", "Small businesses"].map((item) => (
              <div key={item}>
                <FontAwesomeIcon icon={faStore} aria-hidden="true" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="screens-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Real product assets</p>
            <h2 id="screens-title">Auditus-iERP Screenshots</h2>
            <p>
              These images are real assets already present in the local project, reused
              with descriptive alt text.
            </p>
          </div>
          <div className="fs-screenshot-grid">
            {screenshots.map(([src, alt, label]) => (
              <figure key={src}>
                <img src={src} alt={alt} loading="lazy" width="640" height="390" />
                <figcaption>{label}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section fs-why" aria-labelledby="why-title">
        <div className="billing-container billing-two-col">
          <div>
            <p className="billing-kicker">Why Auditus-iERP</p>
            <h2 id="why-title">Why Choose Auditus-iERP?</h2>
          </div>
          <div className="billing-copy">
            <p>
              Auditus-iERP focuses on integrated business management. Billing,
              accounting, inventory, POS, GST, sales, purchases, payments, expenses,
              customers, suppliers, and reports share one connected product ecosystem.
            </p>
            <p>
              That centralized information helps teams work from the same operational
              records, reduce repeated entry, and review day-to-day business activity
              with clearer reporting.
            </p>
          </div>
        </div>
      </section>

      <section className="billing-section billing-faq-section" aria-labelledby="faq-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">FAQ</p>
            <h2 id="faq-title">Features & Business Solutions Questions</h2>
          </div>
          <div className="billing-faq-list">
            {faqs.map(([q, a], index) => {
              const open = openFaq === index;
              return (
                <article className={`billing-faq-item ${open ? "is-open" : ""}`} key={q}>
                  <h3>
                    <button
                      type="button"
                      aria-expanded={open}
                      aria-controls={`features-solutions-faq-${index}`}
                      onClick={() => setOpenFaq(open ? null : index)}
                    >
                      <span className="billing-faq-symbol" aria-hidden="true">{open ? "-" : "+"}</span>
                      {q}
                    </button>
                  </h3>
                  <div
                    id={`features-solutions-faq-${index}`}
                    className="billing-faq-answer"
                    aria-hidden={!open}
                  >
                    <p>{a}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="billing-final">
        <div className="billing-container">
          <div className="billing-final-panel">
            <FontAwesomeIcon icon={faShieldHalved} aria-hidden="true" />
            <p className="billing-final-kicker">Integrated business management</p>
            <h2>Explore What Auditus-iERP Can Do for Your Business</h2>
            <p>
              See how Auditus-iERP can connect billing, accounts, inventory, POS, GST,
              sales, purchases, customers, suppliers, payments, expenses, and reports.
            </p>
            <div className="billing-actions">
              <Link to="/Demo" className="billing-btn billing-btn-primary">
                Book a Demo <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </Link>
              <Link to="/Trial" className="billing-btn billing-btn-secondary">
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
